/* eslint-disable no-underscore-dangle */

export class ResponseError extends Error {
  constructor(statusCode, ...args) {
    super(statusCode, ...args);
    this.name = 'ResponseError';
    this.statusCode = statusCode;
  }
}

export const verifyResponse = response => {
  if (response.ok) {
    return response;
  }

  throw new ResponseError(response.status);
};

export const toJSON = response =>
  // fetch-mock does not set a `content-type` header by default, otherwise we could parse based on that
  response.text().then(text => {
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  });

/**
 * API helper
 *
 * @module API
 * @param {Object} config A configuration object
 * @param {String} config.baseUrl The base URL for making requests, it will be prepended to the final URL parameter
 * @param {String} config.defaultHeaders A set of default headers that will be included with each request.
 * @param {Function} config.getAccessToken The function to call for getting an access token
 * @param {Function} config.mapResponse A function to process the `Response` object
 * @param {Function} config.getCacheKey A function that return a cache key based on the outgoing request (defaults to return the request's URI)
 * @returns {API} instance
 *
 * @example
 * // basic usage
 * const accessTokenManager = new AccessTokenManager({
 *   url: `https://generic.wg.spotify.com/creator-auth-proxy/v1/web/token?client_id=${CLIENT_ID}`,
 *   options: { credentials: 'include' },
 * });
 *
 * const api = new API({ getAccessToken: accessTokenManager.get });
 * await api.get('https://api.spotify.com/v1/me');
 *
 * @example
 * // advanced usage
 * const api = new API({
 *   baseUrl: 'https://api.spotify.com',
 *   defaultHeaders: { 'Spotify-App-Version': '1.0.0', 'App-Platform': 'Browser' },
 *   getAccessToken: async () => 'some-access-token',
 *   mapResponse: () => console.log,
 * });
 * await api.get('/v1/me');
 */
export class API {
  constructor({
    baseUrl = '',
    defaultHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    getAccessToken = () => Promise.resolve(),
    mapResponse = response => response,
    getCacheKey = requestConfig => requestConfig.url,
  } = {}) {
    this._baseUrl = baseUrl;
    this._defaultHeaders = defaultHeaders;
    this._getAccessToken = getAccessToken;
    this._mapResponse = mapResponse;
    this._getCacheKey = getCacheKey;

    this._getCache = new Map();
  }

  /**
   * Main method for performing `fetch` requests
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the `fetch` API
   * @return {Promise} `fetch` response
   */
  request = (url, options = {}) =>
    this._getAccessToken()
      .then(token => {
        const tokenHeaders = {};

        if (token) {
          tokenHeaders.Authorization = `Bearer ${token}`;
        }

        const headers = new Headers({
          ...this._defaultHeaders,
          ...tokenHeaders,
          ...options.headers,
        });

        const isContentTypeJSON =
          headers.get('Content-Type') === 'application/json';
        const body =
          typeof options.body === 'object' && isContentTypeJSON
            ? JSON.stringify(options.body)
            : options.body;

        return {
          url: this._baseUrl + url,
          options: {
            ...options,
            headers,
            body,
          },
        };
      })
      .then(requestConfig => {
        if (requestConfig.options.method === 'GET') {
          const cacheKey = this._getCacheKey(requestConfig);

          if (!this._getCache.has(cacheKey)) {
            const promise = fetch(requestConfig.url, requestConfig.options)
              .then(response =>
                response.text().then(data => ({ data, response })),
              )
              .catch(error => {
                this._getCache.delete(cacheKey);
                throw error;
              })
              .then(payload => {
                this._getCache.delete(cacheKey);
                return payload;
              });

            this._getCache.set(cacheKey, promise);
          }

          return (
            this._getCache
              .get(cacheKey)
              // if data is empty then we use null for body.
              // @see https://ghe.spotify.net/creator/web-shared/issues/4
              .then(
                ({ data, response }) => new Response(data || null, response),
              )
          );
        }
        return fetch(requestConfig.url, requestConfig.options);
      })
      .catch(error => {
        // thanks for nothing Microsoft Edge...
        // @see https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8653298/
        if (error instanceof window.DOMException && error.code === 17) {
          return new Response(null, { status: 401 });
        }

        throw error;
      })
      .then(this._mapResponse);

  /**
   * Shorthand method for performing a GET `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */
  get = (url, options) => this.request(url, { ...options, method: 'GET' });

  /**
   *
   * Shorthand method for performing a POST `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */
  post = (url, options) => this.request(url, { ...options, method: 'POST' });

  /**
   * Shorthand method for performing a PATCH `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */
  patch = (url, options) => this.request(url, { ...options, method: 'PATCH' });

  /**
   * Shorthand method for performing a PUT `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */
  put = (url, options) => this.request(url, { ...options, method: 'PUT' });

  /**
   * Shorthand method for performing a DELETE `fetch` request
   * @param {String} url The URL for the request
   * @param {Object} [options] Options for the request
   * @return {Promise} `fetch` response
   */
  delete = (url, options) =>
    this.request(url, { ...options, method: 'DELETE' });
}
